import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  CalendarIcon,
  CircleChevronRight,
  CircleHelp,
  ClipboardList,
  Factory,
  InboxIcon,
  LayoutDashboard,
  LucideAngularModule,
  PackageIcon,
  Settings,
  ShelvingUnit,
  UsersIcon,
} from 'lucide-angular';
import { MenuItem } from './sidebar.types';
import { CONTACT_URL, KB_BASE_URL } from '../../../configs/help-articles.config';
import { SidebarService } from '../../../services/sidebar/sidebar.service';
import { NgOptimizedImage } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { Click } from '../../../directives/click/click';
import { OrganizationService } from '../../../services/organization/organization.service';
import { PermissionService } from '../../../services/permission/permission.service';
import { Permission } from '../../../enums/permission.enum';
import { OrganizationModule } from '../../../enums/organization-module.enum';
import { ModalService } from '../../../services/modal/modal.service';
import { UpgradeModal, UpgradeModalData } from '../../modals/upgrade-modal/upgrade-modal';
import {
  MODULE_REQUIRED_PLAN,
  PLAN_BADGE_COLOR,
  PLAN_DISPLAY_NAME,
} from '../../../configs/module-plan.config';
import { PurchaseOrdersService } from '../../../services/purchase-orders/purchase-orders.service';
import { derivedAsync } from 'ngxtension/derived-async';
import { catchError, map, of } from 'rxjs';

@Component({
  selector: 'skk-sidebar',
  imports: [LucideAngularModule, NgOptimizedImage, RouterLink, RouterLinkActive, Click],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Sidebar {
  private readonly router = inject(Router);
  private readonly organizationService = inject(OrganizationService);
  private readonly permissionService = inject(PermissionService);
  private readonly modalService = inject(ModalService);
  private readonly sidebarService = inject(SidebarService);
  private readonly purchaseOrdersService = inject(PurchaseOrdersService);

  /** Submitted purchase orders badge - producer context only, gated the same
   * way as the menu item itself so no request fires without access. */
  private readonly incomingOrdersCount = derivedAsync(
    () => {
      if (this.context() === 'portal') return of(0);
      const modules = this.organizationService.organization()?.subscription?.modules ?? [];
      if (
        !this.permissionService.can(Permission.OrdersRead) ||
        !modules.includes(OrganizationModule.Orders)
      ) {
        return of(0);
      }
      return this.purchaseOrdersService.getSummary().pipe(
        map((summary) => summary.submittedCount),
        catchError(() => of(0)),
      );
    },
    { initialValue: 0 },
  );

  readonly CircleChevronRight = CircleChevronRight;
  readonly CircleHelp = CircleHelp;

  // The buyer-side portal shares this sidebar shell; it only swaps the menu
  // items. Settings for buyers is deliberately absent until the buyer-side
  // account area (own subscription tiers for wholesalers) is built - showing a
  // producer-shaped settings page there would expose team/billing screens that
  // do not apply yet.
  readonly context = input<'producer' | 'portal'>('producer');

  // Public knowledge base + contact page live on the marketing site; open in a
  // new tab. Deliberately shown in BOTH contexts - buyers get help and
  // consultation too.
  readonly helpUrl = KB_BASE_URL;
  readonly consultationUrl = CONTACT_URL;

  // Open/closed state lives in a shared service so the topbar hamburger (a
  // separate component) can toggle the mobile drawer.
  readonly isExpanded = this.sidebarService.isOpen;

  readonly menuItems = computed<MenuItem[]>(() => {
    if (this.context() === 'portal') {
      return [
        {
          id: 1,
          name: $localize`:@@portal.nav.suppliers:Suppliers`,
          icon: Factory,
          link: '/portal',
          exact: true,
        },
        {
          id: 2,
          name: $localize`:@@portal.nav.orders:Orders`,
          icon: ClipboardList,
          link: '/portal/orders',
        },
      ];
    }

    const modules = this.organizationService.organization()?.subscription?.modules ?? [];
    const can = (p: Permission) => this.permissionService.can(p);
    const isLocked = (module: OrganizationModule) => !modules.includes(module);

    return [
      ...(can(Permission.DashboardView)
        ? [
            {
              id: 1,
              name: $localize`:@@sidebar.menu.dashboard:Dashboard`,
              icon: LayoutDashboard,
              link: '/',
              exact: true,
            },
          ]
        : []),
      ...(can(Permission.TasksRead) || can(Permission.TasksMeRead)
        ? [
            {
              id: 2,
              name: $localize`:@@sidebar.menu.calendar:Calendar`,
              icon: CalendarIcon,
              link: '/calendar',
              module: OrganizationModule.Calendar,
              locked: isLocked(OrganizationModule.Calendar),
            },
          ]
        : []),
      ...(can(Permission.ProductsRead)
        ? [
            {
              id: 2,
              name: $localize`:@@sidebar.menu.products:Products`,
              icon: PackageIcon,
              link: '/products',
              module: OrganizationModule.Products,
              locked: isLocked(OrganizationModule.Products),
            },
          ]
        : []),
      ...(can(Permission.OrdersRead)
        ? [
            {
              id: 3,
              name: $localize`:@@sidebar.menu.sales:Orders`,
              icon: ClipboardList,
              link: '/orders',
              module: OrganizationModule.Orders,
              locked: isLocked(OrganizationModule.Orders),
            },
            {
              id: 8,
              name: $localize`:@@sidebar.menu.purchaseOrders:Purchase orders`,
              icon: InboxIcon,
              link: '/purchase-orders',
              module: OrganizationModule.Orders,
              locked: isLocked(OrganizationModule.Orders),
              count: this.incomingOrdersCount(),
            },
          ]
        : []),
      ...(can(Permission.InventoryRead)
        ? [
            {
              id: 4,
              name: $localize`:@@sidebar.menu.inventory:Inventory`,
              icon: ShelvingUnit,
              link: '/inventory',
              module: OrganizationModule.Inventory,
              locked: isLocked(OrganizationModule.Inventory),
            },
          ]
        : []),
      ...(can(Permission.CustomersRead)
        ? [
            {
              id: 5,
              name: $localize`:@@sidebar.menu.customers:Customers`,
              icon: UsersIcon,
              link: '/customers',
              module: OrganizationModule.Customers,
              locked: isLocked(OrganizationModule.Customers),
            },
          ]
        : []),
      // Settings is available to every authenticated user - the personal
      // "User settings" tab needs no permission. Management tabs inside are
      // gated individually (organization, team, billing, integrations).
      {
        id: 7,
        name: $localize`:@@sidebar.menu.settings:Settings`,
        icon: Settings,
        link: '/settings',
      },
    ];
  });

  constructor() {
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.sidebarService.close());
  }

  toggle(): void {
    this.sidebarService.toggle();
  }

  onLockedModuleClick(item: MenuItem): void {
    if (!item.module) return;
    const data: UpgradeModalData = { module: item.module };
    this.modalService.open(UpgradeModal, data);
  }

  planBadgeVariant(module: OrganizationModule): string {
    return PLAN_BADGE_COLOR[MODULE_REQUIRED_PLAN[module]];
  }

  planLabel(module: OrganizationModule): string {
    return PLAN_DISPLAY_NAME[MODULE_REQUIRED_PLAN[module]];
  }
}
